import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import axios from 'axios';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { toast } from 'sonner';
import { motion } from 'framer-motion';
import { 
  Shield, FileText, Settings, LogOut, BarChart3,
  Search, Filter, ChevronRight, X, Save
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const WARRANTY_STATUSES = [
  'Solicitud Abierta',
  'En Tránsito al Servicio Técnico',
  'Entregado en Servicio Técnico',
  'Haciendo Comprobaciones',
  'En Reparación',
  'Reparado',
  'Sustituido / Reemplazado',
  'Enviado al Cliente',
  'RMA Completado'
];

const getStatusClass = (status) => {
  const statusMap = {
    'Solicitud Abierta': 'status-open',
    'En Tránsito al Servicio Técnico': 'status-transit',
    'Entregado en Servicio Técnico': 'status-transit',
    'Haciendo Comprobaciones': 'status-checking',
    'En Reparación': 'status-repairing',
    'Reparado': 'status-repaired',
    'Sustituido / Reemplazado': 'status-replaced',
    'Enviado al Cliente': 'status-shipped',
    'RMA Completado': 'status-completed'
  };
  return statusMap[status] || 'status-open';
};

const AdminWarranties = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [warranties, setWarranties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [selectedWarranty, setSelectedWarranty] = useState(null);
  const [editData, setEditData] = useState({
    status: '',
    technician_notes: '',
    repair_description: '',
    outbound_tracking: ''
  });
  const [saving, setSaving] = useState(false);

  const fetchWarranties = async () => {
    setLoading(true);
    try {
      const params = filterStatus !== 'all' ? `?status=${filterStatus}` : '';
      const response = await axios.get(`${API_URL}/api/admin/warranty${params}`, { withCredentials: true });
      setWarranties(response.data);
    } catch (error) {
      console.error('Error fetching warranties:', error);
      toast.error('Error al cargar las garantías');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchWarranties();
  }, [filterStatus]);

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  const openEditDialog = (warranty) => {
    setSelectedWarranty(warranty);
    setEditData({
      status: warranty.status,
      technician_notes: warranty.technician_notes || '',
      repair_description: warranty.repair_description || '',
      outbound_tracking: warranty.outbound_tracking || ''
    });
  };

  const handleSave = async () => {
    if (!selectedWarranty) return;
    setSaving(true);
    try {
      await axios.put(
        `${API_URL}/api/admin/warranty/${selectedWarranty.request_id}`,
        editData,
        { withCredentials: true }
      );
      toast.success('Garantía actualizada');
      setSelectedWarranty(null);
      fetchWarranties();
    } catch (error) {
      toast.error('Error al actualizar');
    } finally {
      setSaving(false);
    }
  };

  const filteredWarranties = warranties.filter(w => 
    w.product_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    w.full_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    w.request_id?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    w.order_id?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="min-h-screen bg-[#F5F5F7]" data-testid="admin-warranties">
      {/* Sidebar */}
      <aside className="fixed left-0 top-0 h-screen w-64 bg-[#0A0A0A] text-white flex flex-col">
        <div className="p-6">
          <Link to="/admin" className="flex items-center gap-2">
            <div className="w-10 h-10 bg-[#0047FF] rounded-lg flex items-center justify-center">
              <span className="text-white font-bold text-xl">A</span>
            </div>
            <div>
              <span className="font-heading font-bold text-lg">Arreglatelo</span>
              <p className="text-xs text-[#A1A1AA]">Admin Panel</p>
            </div>
          </Link>
        </div>

        <nav className="flex-1 px-4">
          <Link to="/admin" className="flex items-center gap-3 px-4 py-3 rounded-lg hover:bg-white/5 transition-colors mb-1">
            <BarChart3 className="w-5 h-5 text-[#A1A1AA]" />
            <span className="text-[#A1A1AA]">Dashboard</span>
          </Link>
          <Link to="/admin/warranties" className="flex items-center gap-3 px-4 py-3 rounded-lg bg-white/10 mb-1">
            <Shield className="w-5 h-5" />
            <span className="font-medium">Garantías</span>
          </Link>
          <Link to="/admin/invoices" className="flex items-center gap-3 px-4 py-3 rounded-lg hover:bg-white/5 transition-colors mb-1">
            <FileText className="w-5 h-5 text-[#A1A1AA]" />
            <span className="text-[#A1A1AA]">Facturas</span>
          </Link>
          <Link to="/admin/settings" className="flex items-center gap-3 px-4 py-3 rounded-lg hover:bg-white/5 transition-colors mb-1">
            <Settings className="w-5 h-5 text-[#A1A1AA]" />
            <span className="text-[#A1A1AA]">Configuración</span>
          </Link>
        </nav>

        <div className="p-4 border-t border-white/10">
          <Button
            variant="ghost"
            className="w-full justify-start text-[#A1A1AA] hover:text-white hover:bg-white/5"
            onClick={handleLogout}
          >
            <LogOut className="w-4 h-4 mr-2" />
            Cerrar Sesión
          </Button>
        </div>
      </aside>

      {/* Main Content */}
      <main className="ml-64 p-8">
        <div className="max-w-6xl">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h1 className="text-2xl font-bold">Gestión de Garantías</h1>
              <p className="text-[#52525B]">{warranties.length} solicitudes</p>
            </div>
          </div>

          {/* Filters */}
          <div className="flex gap-4 mb-6">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#A1A1AA]" />
              <Input
                placeholder="Buscar por nombre, producto, ID..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
                data-testid="search-input"
              />
            </div>
            <Select value={filterStatus} onValueChange={setFilterStatus}>
              <SelectTrigger className="w-64" data-testid="filter-select">
                <Filter className="w-4 h-4 mr-2" />
                <SelectValue placeholder="Filtrar por estado" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos los estados</SelectItem>
                {WARRANTY_STATUSES.map((s) => (
                  <SelectItem key={s} value={s}>{s}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Table */}
          {loading ? (
            <div className="flex items-center justify-center py-20">
              <div className="animate-spin rounded-full h-10 w-10 border-t-2 border-b-2 border-[#0047FF]"></div>
            </div>
          ) : (
            <div className="bg-white rounded-xl border border-[#E5E7EB] overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-[#F5F5F7] border-b border-[#E5E7EB]">
                    <tr>
                      <th className="text-left px-4 py-3 text-sm font-semibold text-[#52525B]">Referencia</th>
                      <th className="text-left px-4 py-3 text-sm font-semibold text-[#52525B]">Cliente</th>
                      <th className="text-left px-4 py-3 text-sm font-semibold text-[#52525B]">Producto</th>
                      <th className="text-left px-4 py-3 text-sm font-semibold text-[#52525B]">Estado</th>
                      <th className="text-left px-4 py-3 text-sm font-semibold text-[#52525B]">Fecha</th>
                      <th className="text-left px-4 py-3 text-sm font-semibold text-[#52525B]">Acciones</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredWarranties.map((warranty, index) => (
                      <tr 
                        key={warranty.request_id} 
                        className="border-b border-[#E5E7EB] hover:bg-[#F5F5F7]/50 transition-colors"
                        data-testid={`warranty-row-${index}`}
                      >
                        <td className="px-4 py-3">
                          <span className="font-mono text-sm">
                            #{warranty.request_id?.slice(0, 8).toUpperCase()}
                          </span>
                        </td>
                        <td className="px-4 py-3">
                          <p className="font-medium">{warranty.full_name}</p>
                          <p className="text-xs text-[#A1A1AA]">{warranty.email}</p>
                        </td>
                        <td className="px-4 py-3">
                          <p className="font-medium">{warranty.product_name}</p>
                          <p className="text-xs text-[#A1A1AA]">{warranty.purchase_source}</p>
                        </td>
                        <td className="px-4 py-3">
                          <span className={`status-badge ${getStatusClass(warranty.status)}`}>
                            {warranty.status}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-sm text-[#52525B]">
                          {new Date(warranty.created_at).toLocaleDateString('es-ES')}
                        </td>
                        <td className="px-4 py-3">
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => openEditDialog(warranty)}
                            data-testid={`edit-btn-${index}`}
                          >
                            Gestionar
                            <ChevronRight className="w-4 h-4 ml-1" />
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {filteredWarranties.length === 0 && (
                <div className="p-12 text-center text-[#A1A1AA]">
                  No se encontraron garantías
                </div>
              )}
            </div>
          )}
        </div>
      </main>

      {/* Edit Dialog */}
      <Dialog open={!!selectedWarranty} onOpenChange={() => setSelectedWarranty(null)}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>
              Gestionar Garantía #{selectedWarranty?.request_id?.slice(0, 8).toUpperCase()}
            </DialogTitle>
          </DialogHeader>
          
          {selectedWarranty && (
            <div className="space-y-4 mt-4">
              {/* Info Summary */}
              <div className="bg-[#F5F5F7] rounded-lg p-4">
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <p className="text-[#A1A1AA]">Cliente</p>
                    <p className="font-medium">{selectedWarranty.full_name}</p>
                  </div>
                  <div>
                    <p className="text-[#A1A1AA]">Producto</p>
                    <p className="font-medium">{selectedWarranty.product_name}</p>
                  </div>
                  <div>
                    <p className="text-[#A1A1AA]">Email</p>
                    <p className="font-medium">{selectedWarranty.email}</p>
                  </div>
                  <div>
                    <p className="text-[#A1A1AA]">Teléfono</p>
                    <p className="font-medium">{selectedWarranty.phone}</p>
                  </div>
                </div>
                <div className="mt-3 pt-3 border-t border-[#E5E7EB]">
                  <p className="text-[#A1A1AA] text-sm">Problema</p>
                  <p className="text-sm">{selectedWarranty.problem_description}</p>
                </div>
              </div>

              {/* Status */}
              <div>
                <Label>Estado</Label>
                <Select 
                  value={editData.status} 
                  onValueChange={(v) => setEditData(prev => ({ ...prev, status: v }))}
                >
                  <SelectTrigger className="mt-1" data-testid="status-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {WARRANTY_STATUSES.map((s) => (
                      <SelectItem key={s} value={s}>{s}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Technician Notes */}
              <div>
                <Label>Notas del Técnico (interno)</Label>
                <Textarea
                  value={editData.technician_notes}
                  onChange={(e) => setEditData(prev => ({ ...prev, technician_notes: e.target.value }))}
                  className="mt-1"
                  placeholder="Notas internas sobre el estado del dispositivo..."
                  data-testid="technician-notes-input"
                />
              </div>

              {/* Repair Description */}
              <div>
                <Label>Trabajo Realizado (visible para cliente)</Label>
                <Textarea
                  value={editData.repair_description}
                  onChange={(e) => setEditData(prev => ({ ...prev, repair_description: e.target.value }))}
                  className="mt-1"
                  placeholder="Describe el trabajo realizado..."
                  data-testid="repair-description-input"
                />
              </div>

              {/* Outbound Tracking */}
              <div>
                <Label>Tracking de Envío al Cliente</Label>
                <Input
                  value={editData.outbound_tracking}
                  onChange={(e) => setEditData(prev => ({ ...prev, outbound_tracking: e.target.value }))}
                  className="mt-1 font-mono"
                  placeholder="PQ43B404AA015110128001N"
                  data-testid="outbound-tracking-input"
                />
              </div>

              {/* Actions */}
              <div className="flex justify-end gap-3 pt-4">
                <Button variant="outline" onClick={() => setSelectedWarranty(null)}>
                  Cancelar
                </Button>
                <Button 
                  className="bg-[#0047FF] hover:bg-[#003BCC]"
                  onClick={handleSave}
                  disabled={saving}
                  data-testid="save-btn"
                >
                  {saving ? 'Guardando...' : 'Guardar Cambios'}
                  <Save className="w-4 h-4 ml-2" />
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminWarranties;
